import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, Trash2, Plus } from "lucide-react";
import { listQuotes, deleteQuote, formatUSD } from "@/lib/api";

export default function QuoteHistory() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = () => {
    setLoading(true);
    listQuotes()
      .then(setQuotes)
      .catch(() => toast.error("Failed to load quotes"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const filtered = useMemo(() => {
    return quotes.filter((q) => {
      if (statusFilter !== "all" && q.status !== statusFilter) return false;
      if (!search) return true;
      const s = search.toLowerCase();
      return (
        q.quote_number?.toLowerCase().includes(s) ||
        q.project_title?.toLowerCase().includes(s) ||
        q.client_name?.toLowerCase().includes(s) ||
        q.client_email?.toLowerCase().includes(s)
      );
    });
  }, [quotes, search, statusFilter]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this quote? This cannot be undone.")) return;
    try {
      await deleteQuote(id);
      toast.success("Quote deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const statuses = ["all", "draft", "sent", "accepted", "declined"];

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-8 lg:px-12 py-12 lg:py-16" data-testid="quotes-page">
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
        <div>
          <div className="text-label mb-4">№ 003 — Archive</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none">
            Quote History
          </h1>
        </div>
        <Link to="/calculator" className="btn-primary self-start" data-testid="history-new-quote">
          <Plus className="w-4 h-4" strokeWidth={1.5} /> New Quote
        </Link>
      </header>

      {/* Filters */}
      <div className="border border-[#050505] p-4 sm:p-5 mb-px flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="flex-1 flex items-center gap-2 border border-[#e5e5e5] px-3 py-2">
          <Search className="w-4 h-4 text-[#525252]" strokeWidth={1.5} />
          <input
            type="text"
            placeholder="Search by quote #, project, client…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="search-input"
            className="flex-1 outline-none text-sm bg-transparent font-mono"
          />
        </div>
        <div className="flex gap-px">
          {statuses.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`filter-${s}`}
              className={`px-3 h-10 text-[11px] uppercase tracking-[0.18em] font-bold border border-[#050505] transition-colors ${
                statusFilter === s ? "bg-[#050505] text-white" : "bg-white text-[#050505] hover:bg-[#f5f5f5]"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="border border-[#050505] border-t-0 overflow-x-auto">
        {loading ? (
          <div className="p-12 text-center text-sm font-mono text-[#525252]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center">
            <div className="font-display text-2xl font-bold mb-2">No quotes match.</div>
            <div className="text-sm text-[#525252]">Try clearing filters or creating a new quote.</div>
          </div>
        ) : (
          <table className="w-full" data-testid="quotes-table">
            <thead>
              <tr className="border-b border-[#050505]">
                {["Quote #", "Project", "Client", "Specs", "Qty", "Status", "Total", ""].map((h) => (
                  <th key={h} className="text-left px-4 py-4 text-[11px] uppercase tracking-[0.2em] text-[#525252] whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((q) => (
                <tr key={q.id} className="border-b border-[#e5e5e5] hover:bg-[#f5f5f5] transition-colors" data-testid={`quote-row-${q.id}`}>
                  <td className="px-4 py-4">
                    <Link to={`/quotes/${q.id}`} className="font-mono text-sm font-medium ghost-link">
                      {q.quote_number}
                    </Link>
                    <div className="text-[10px] font-mono text-[#525252] mt-1">
                      {new Date(q.created_at).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm">
                    {q.project_title || <span className="text-[#525252]">Untitled</span>}
                  </td>
                  <td className="px-4 py-4 text-sm">
                    {q.client_name || <span className="text-[#525252]">—</span>}
                    {q.client_email && (
                      <div className="text-[11px] text-[#525252] font-mono">{q.client_email}</div>
                    )}
                  </td>
                  <td className="px-4 py-4 text-xs font-mono text-[#525252]">
                    {q.labels.trim_size} · {q.spec.page_count}pp · {q.labels.color_mode}
                  </td>
                  <td className="px-4 py-4 font-mono text-sm">{q.spec.quantity}</td>
                  <td className="px-4 py-4">
                    <span className={`status-pill status-${q.status}`}>{q.status}</span>
                  </td>
                  <td className="px-4 py-4 font-mono text-sm font-bold whitespace-nowrap">
                    {formatUSD(q.breakdown.total)}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <button
                      onClick={() => handleDelete(q.id)}
                      data-testid={`delete-${q.id}`}
                      className="text-[#525252] hover:text-[#FF3333] transition-colors"
                      aria-label="Delete quote"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
